import { datadogLogs } from '@datadog/browser-logs'

/**
 * Standard log levels
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/**
 * Structure for log metadata that can be attached to log entries
 */
export interface LogMetadata {
  [key: string]: any
}

/**
 * Error source types for categorizing errors
 */
export type ErrorSource = 
  | 'next_client_exception'  // Default Next.js client-side errors
  | 'next_global_error'      // Errors caught by global error handler
  | 'next_hydration_error'   // Next.js hydration mismatches
  | 'react_error'            // General React component errors
  | 'api_error'              // API request errors
  | 'auth_error'             // Authentication errors
  | 'data_error'             // Data processing errors
  | 'unknown'                // Uncategorized errors

/**
 * Common logger for standardized logging across the application
 * Logs to console with structured format and to Datadog when available
 */
export const logger = {
  /**
   * Log debugging information (development only)
   */
  debug: (message: string, metadata?: LogMetadata): void => {
    if (process.env.NODE_ENV !== 'production') {
      console.debug(`[DEBUG] ${message}`, metadata || '')
    }
  },

  /**
   * Log general information
   */
  info: (message: string, metadata?: LogMetadata): void => {
    console.info(`[INFO] ${message}`, metadata || '')
    
    try {
      datadogLogs.logger.info(message, {
        ...metadata,
        timestamp: new Date().toISOString()
      })
    } catch (e) {
      // Silently fail if Datadog isn't initialized
    }
  },

  /**
   * Log warnings
   */
  warn: (message: string, metadata?: LogMetadata): void => {
    console.warn(`[WARN] ${message}`, metadata || '')
    
    try {
      datadogLogs.logger.warn(message, {
        ...metadata,
        timestamp: new Date().toISOString()
      })
    } catch (e) {
      // Silently fail if Datadog isn't initialized
    }
  },

  /**
   * Log errors with detailed context
   * This method is optimized to work with Datadog's forwardConsoleLogs feature
   */
  error: (
    message: string, 
    error?: Error | unknown, 
    source: ErrorSource = 'unknown',
    metadata?: LogMetadata
  ): void => {
    // Extract error details if an error object is provided
    const errorObject = error instanceof Error ? error : null
    const errorMessage = errorObject?.message || (error ? String(error) : 'Unknown error')
    const errorStack = errorObject?.stack
    const errorName = errorObject?.name || 'Error'
    
    // Create a consistent error context object
    const errorContext = {
      error_message: errorMessage,
      error_stack: errorStack,
      error_name: errorName,
      error_caused_by: source,
      url: typeof window !== 'undefined' ? window.location.href : '',
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      user_agent: typeof window !== 'undefined' ? navigator.userAgent : '',
      timestamp: new Date().toISOString(),
      ...metadata
    }
    
    // First log to console to ensure Datadog picks it up via forwardConsoleLogs
    console.error(`[ERROR] [${source}] ${message}:`, errorMessage)
    
    if (errorStack) {
      console.error('Error Stack:', errorStack)
    }
    
    if (metadata) {
      console.error('Additional Context:', metadata)
    }
    
    // Then try to log directly to Datadog for redundancy
    try {
      datadogLogs.logger.error(message, errorContext)
    } catch (e) {
      // If direct Datadog logging fails, log that error too
      console.error('Failed to log to Datadog:', e)
    }
  }
}

/**
 * Helper function to log generic API errors
 */
export function logApiError(
  endpoint: string, 
  error: unknown, 
  additionalContext?: LogMetadata
): void {
  logger.error(
    `API Error: ${endpoint}`, 
    error, 
    'api_error',
    additionalContext
  )
}

/**
 * Helper function to log Next.js client-side errors
 */
export function logNextClientError(
  error: Error, 
  componentInfo?: string,
  additionalContext?: LogMetadata
): void {
  logger.error(
    `Next.js Client Error${componentInfo ? ` in ${componentInfo}` : ''}`, 
    error, 
    'next_client_exception',
    additionalContext
  )
}

/**
 * Initialize Datadog logging if it hasn't been initialized yet
 * Also sets up global error handlers for window.onerror and unhandledrejection
 */
export function initLogging(): void {
  if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN) {
    return
  }
  
  try {
    // Initialize Datadog logging
    datadogLogs.init({
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      forwardConsoleLogs: 'all',
    })
    
    // Get app version from environment variables (prefer Vercel variables)
    const appVersion = 
      typeof window !== 'undefined' && (window as any).NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ? 
        (window as any).NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA : 
        process.env.NEXT_PUBLIC_APP_VERSION || 'unknown';
        
    // Set global context for all logs
    datadogLogs.setGlobalContext({
      env: process.env.NODE_ENV,
      service: 'web-app',
      version: appVersion,
      vercel_env: process.env.NEXT_PUBLIC_VERCEL_ENV || 'unknown',
    })
    
    // Set up window.onerror handler
    const originalOnError = window.onerror
    window.onerror = function(message, source, lineno, colno, error) {
      // Use our logger to log the error
      logger.error(
        'Uncaught Window Error', 
        error || message, 
        'unknown',
        {
          error_source: source,
          error_line: lineno,
          error_column: colno,
          called_from: 'window.onerror'
        }
      )
      
      // Call the original handler if it exists
      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error)
      }
      
      return false
    }
    
    // Set up unhandled promise rejection handler
    window.addEventListener('unhandledrejection', function(event) {
      // Use our logger to log the error
      logger.error(
        'Unhandled Promise Rejection', 
        event.reason, 
        'unknown',
        {
          called_from: 'unhandledrejection',
          promise_event: true
        }
      )
    })
    
    // Log Next.js hydration errors specifically by intercepting console.error
    const originalConsoleError = console.error
    console.error = function(...args) {
      const errorString = args.join(' ')
      
      // Check if this is a Next.js hydration error
      if (
        errorString.includes('Hydration failed') || 
        errorString.includes('Text content did not match') ||
        errorString.includes('Expected server HTML to contain')
      ) {
        // Use our logger directly so we don't create a loop
        try {
          datadogLogs.logger.error('Next.js Hydration Error', {
            error_message: errorString,
            error_caused_by: 'next_hydration_error',
            url: window.location.href,
            timestamp: new Date().toISOString(),
          })
        } catch (e) {
          // Silently fail as we're already in an error handler
        }
      }
      
      // Call the original console.error
      originalConsoleError.apply(console, args)
    }
    
    logger.debug('Logging initialized successfully')
  } catch (error) {
    console.error('Failed to initialize logging:', error)
  }
} 